import { Suspense, lazy, useEffect } from "react";
import ScrollToTop from "./helpers/scroll-top";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import ShopCategory from "./pages/shop/ShopCategory";
import { useStoreInfo } from "./hooks/storeInfoHooks/useStoreInfo";
import SEO from "./components/seo";
import { useFavicon } from "@uidotdev/usehooks";
import { useAbout } from "./hooks/aboutHooks/useAbout";
import { useProducts } from "./hooks/productsHooks/useProducts";
import { useBanners } from "./hooks/bannersHooks/useBanners";
import { useCategories } from "./hooks/categoriesHooks/useCategories";
import { useTestimonials } from "./hooks/testimonialsHooks/useTestimonials";
import WhatsappButton from "./components/WhatsappButton";
import OrderStatus from "./pages/other/order-status";

const Home = lazy(() => import("./pages/home/Home"));

// shop pages
const Shop = lazy(() => import("./pages/shop/Shop"));

// product pages
const Product = lazy(() => import("./pages/shop-product/Product"));

// other pages
const Offers = lazy(() => import("./pages/other/Offers"));
const About = lazy(() => import("./pages/other/About"));
const Contact = lazy(() => import("./pages/other/Contact"));
const Services = lazy(() => import("./pages/other/Services"));
const MyAccount = lazy(() => import("./pages/other/MyAccount"));
const LoginRegister = lazy(() => import("./pages/other/LoginRegister"));
const Cart = lazy(() => import("./pages/other/Cart"));
const Wishlist = lazy(() => import("./pages/other/Wishlist"));
const Compare = lazy(() => import("./pages/other/Compare"));
const Checkout = lazy(() => import("./pages/other/Checkout"));
const NotFound = lazy(() => import("./pages/other/NotFound"));
const Privacy = lazy(() => import("./pages/other/Privacy"));
const Returns = lazy(() => import("./pages/other/Returns"));
const Shipping = lazy(() => import("./pages/other/shipping-info"));

const App = () => {
  const { data: storeInfo, isLoading: isLoadingStoreInfo } = useStoreInfo();
  const { data: about } = useAbout();
  const { data: products } = useProducts();
  const { data: banners } = useBanners();
  const { data: categories } = useCategories();
  const { data: testimonials } = useTestimonials();

  useFavicon(
    `${import.meta.env.VITE_REACT_APP_API_URL}/${storeInfo?.[0]?.logo}`
  );

  useEffect(() => {
    if (Date.now() >= JSON.parse(localStorage.getItem("tokenExpiryTime"))) {
      localStorage.removeItem("token");
      localStorage.removeItem("tokenExpiryTime");
    }
  }, [localStorage.getItem("token")]);

  return (
    <>
      <SEO
        title={storeInfo?.[0]?.name}
        description={storeInfo?.[0]?.seo_description}
        keywords={storeInfo?.[0]?.seo_keywords}
      />
      <Router>
        <ScrollToTop>
          <Suspense
            fallback={
              <div className="flone-preloader-wrapper">
                <div className="flone-preloader">
                  <span></span>
                  <span></span>
                </div>
              </div>
            }
          >
            <Routes>
              <Route path={"/"} element={<Home />} />

              {/* Shop pages */}
              <Route path={"/shop"} element={<Shop />} />
              <Route path={"/category"} element={<Shop />} />
              <Route path={"/category/:id"} element={<ShopCategory />} />

              {/* Shop product pages */}
              <Route path={"/product/:id"} element={<Product />} />

              {/* Other pages */}
              <Route path={"/offers"} element={<Offers />} />
              <Route path={"/about"} element={<About />} />
              <Route path={"/services"} element={<Services />} />
              <Route path={"/contact"} element={<Contact />} />
              <Route path={"/my-account"} element={<MyAccount />} />
              <Route path={"/login-register"} element={<LoginRegister />} />
              <Route path={"/cart"} element={<Cart />} />
              <Route path={"/wishlist"} element={<Wishlist />} />
              <Route path={"/compare"} element={<Compare />} />
              <Route path={"/checkout"} element={<Checkout />} />
              <Route path={"/order"} element={<OrderStatus />} />
              <Route path={"/privacy-policy"} element={<Privacy />} />
              <Route path={"/shipping-info"} element={<Shipping />} />
              <Route path={"/return-policy"} element={<Returns />} />

              {/* Error page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ScrollToTop>
        <WhatsappButton />
      </Router>
    </>
  );
};

export default App;
